/**
 * Image ↔ Base64 helpers: data-URI snippets, size arithmetic, and reading a pasted data
 * URI (or bare Base64) back into bytes with the image type checked against the bytes.
 */

import { decodeBase64Url } from "@/lib/tool-kit/latex-dev/jwt";

export type SnippetFormat =
  | "dataUri"
  | "base64"
  | "html"
  | "css"
  | "markdown"
  | "svgText";

export interface SnippetInput {
  mime: string;
  base64: string;
  /** The SVG source, when the file is an SVG. */
  svgText: string;
  alt: string;
  width: number;
  height: number;
}

const ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const RE_HTML_SRC = /\bsrc\s*=\s*(["'])(data:[\s\S]*?)\1/i;
const RE_CSS_URL = /url\(\s*(["']?)(data:[\s\S]*?)\1\s*\)/i;
const RE_DATA_URI = /^data:([^,;]*)((?:;[^,;]*)*),([\s\S]*)$/i;
const RE_WHITESPACE = /\s+/g;
const RE_QUOTES = /^["'`]+|["'`]+$/g;
const RE_NON_ASCII = /\P{ASCII}/gu;
const RE_ATTR_QUOTE = /"/g;
const RE_HTML_ESCAPE = /[&"<>]/g;
const RE_SVG_START =
  /^\s*(?:<\?xml[\s\S]*?\?>\s*)?(?:<!--[\s\S]*?-->\s*)*<svg[\s>]/i;

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  '"': "&quot;",
  "<": "&lt;",
  ">": "&gt;",
};

const EXTENSIONS: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/x-icon": "ico",
  "image/vnd.microsoft.icon": "ico",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/tiff": "tiff",
  "application/pdf": "pdf",
};

/** Encode bytes as standard Base64 with padding. */
export function encodeBase64(bytes: Uint8Array): string {
  const out: string[] = [];
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const value = a * 65_536 + (b ?? 0) * 256 + (c ?? 0);
    const quad = [
      Math.floor(value / 262_144) % 64,
      Math.floor(value / 4096) % 64,
      Math.floor(value / 64) % 64,
      value % 64,
    ];
    out.push(
      ALPHABET[quad[0]],
      ALPHABET[quad[1]],
      b === undefined ? "=" : ALPHABET[quad[2]],
      c === undefined ? "=" : ALPHABET[quad[3]]
    );
  }
  return out.join("");
}

/** The Base64 length for a given number of bytes: four characters per three bytes, padded. */
export function base64Length(byteLength: number): number {
  return Math.ceil(byteLength / 3) * 4;
}

export interface SizeStats {
  bytes: number;
  base64Chars: number;
  dataUriChars: number;
  /** How much longer the data URI is than the file, as a percentage. */
  overheadPercent: number;
}

export function sizeStats(byteLength: number, mime: string): SizeStats {
  const base64Chars = base64Length(byteLength);
  const dataUriChars = `data:${mime};base64,`.length + base64Chars;
  const overheadPercent =
    byteLength === 0 ? 0 : (dataUriChars / byteLength - 1) * 100;
  return { bytes: byteLength, base64Chars, dataUriChars, overheadPercent };
}

export function extensionFor(mime: string): string {
  return EXTENSIONS[mime.toLowerCase()] ?? "bin";
}

function escapeHtml(text: string): string {
  return text.replace(RE_HTML_ESCAPE, (c) => HTML_ESCAPES[c]);
}

/** SVG as a URL-encoded data URI body: smaller than Base64 for most SVGs. */
export function encodeSvgText(svg: string): string {
  const compact = svg
    .trim()
    .replace(RE_WHITESPACE, " ")
    .replace(RE_ATTR_QUOTE, "'")
    .replaceAll("%", "%25")
    .replaceAll("#", "%23")
    .replaceAll("<", "%3C")
    .replaceAll(">", "%3E");
  return compact.replace(RE_NON_ASCII, (c) => encodeURIComponent(c));
}

/** The text a reader copies for one output format. */
export function buildSnippet(
  format: SnippetFormat,
  input: SnippetInput
): string {
  const dataUri = `data:${input.mime};base64,${input.base64}`;
  const svgUri = `data:image/svg+xml,${encodeSvgText(input.svgText)}`;
  const uri = format === "svgText" ? svgUri : dataUri;
  switch (format) {
    case "base64":
      return input.base64;
    case "html": {
      const size =
        input.width > 0 && input.height > 0
          ? ` width="${input.width}" height="${input.height}"`
          : "";
      return `<img src="${escapeHtml(uri)}" alt="${escapeHtml(input.alt)}"${size}>`;
    }
    case "css":
      return `background-image: url("${uri.replace(RE_ATTR_QUOTE, "%22")}");`;
    case "markdown":
      return `![${input.alt.replaceAll("]", "\\]")}](${uri.replaceAll(")", "%29").replaceAll(" ", "%20")})`;
    default:
      return uri;
  }
}

// ---------------------------------------------------------------------------
// Reading Base64 back
// ---------------------------------------------------------------------------

const SIGNATURES: readonly [string, number[], number?][] = [
  ["image/png", [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  ["image/jpeg", [0xff, 0xd8, 0xff]],
  ["image/gif", [0x47, 0x49, 0x46, 0x38]],
  ["image/bmp", [0x42, 0x4d]],
  ["image/x-icon", [0x00, 0x00, 0x01, 0x00]],
  ["image/tiff", [0x49, 0x49, 0x2a, 0x00]],
  ["image/tiff", [0x4d, 0x4d, 0x00, 0x2a]],
  ["application/pdf", [0x25, 0x50, 0x44, 0x46]],
];

function bytesAre(bytes: Uint8Array, expected: number[], offset = 0): boolean {
  return expected.every((value, i) => bytes[offset + i] === value);
}

function ascii(bytes: Uint8Array, from: number, to: number): string {
  return String.fromCharCode(...bytes.slice(from, to));
}

/** The type the bytes say they are, or null when they match nothing known. */
export function sniffMime(bytes: Uint8Array): string | null {
  for (const [mime, signature, offset] of SIGNATURES) {
    if (bytesAre(bytes, signature, offset ?? 0)) {
      return mime;
    }
  }
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") {
    return "image/webp";
  }
  if (ascii(bytes, 4, 8) === "ftyp") {
    const brand = ascii(bytes, 8, 12);
    if (brand === "avif" || brand === "avis") {
      return "image/avif";
    }
    return brand.startsWith("hei") || brand.startsWith("mif")
      ? "image/heic"
      : null;
  }
  const head = new TextDecoder().decode(bytes.slice(0, 600));
  return RE_SVG_START.test(head) ? "image/svg+xml" : null;
}

export type InputErrorCode = "empty" | "noData" | "badBase64";
export type InputWarning = "mismatch" | "notImage" | "unknownType" | "noPrefix";

export interface ParsedImageInput {
  /** The type to use: what the bytes are, else what the data URI declared. */
  mime: string;
  declaredMime: string;
  sniffedMime: string | null;
  base64: string;
  bytes: Uint8Array;
  /** Where the data was found. */
  source: "dataUri" | "html" | "css" | "raw";
  warnings: InputWarning[];
}

export type InputResult =
  | { ok: true; image: ParsedImageInput }
  | { ok: false; code: InputErrorCode };

function locateData(text: string): {
  source: ParsedImageInput["source"];
  value: string;
} {
  const html = RE_HTML_SRC.exec(text);
  if (html) {
    return { source: "html", value: html[2] };
  }
  const css = RE_CSS_URL.exec(text);
  if (css) {
    return { source: "css", value: css[2] };
  }
  const trimmed = text.trim().replace(RE_QUOTES, "");
  return {
    source: trimmed.toLowerCase().startsWith("data:") ? "dataUri" : "raw",
    value: trimmed,
  };
}

function percentBytes(text: string): Uint8Array {
  let decoded = text;
  try {
    decoded = decodeURIComponent(text);
  } catch {
    // Leave text that is not valid percent-encoding as it is.
  }
  return new TextEncoder().encode(decoded);
}

/** Read an image from a data URI, an `<img>` or CSS snippet holding one, or bare Base64. */
export function parseImageInput(text: string): InputResult {
  if (text.trim() === "") {
    return { ok: false, code: "empty" };
  }
  const found = locateData(text);
  let declaredMime = "";
  let bytes: Uint8Array | null;
  const uri = RE_DATA_URI.exec(found.value);
  if (found.source !== "raw" && uri) {
    declaredMime = uri[1].trim().toLowerCase();
    const isBase64 = uri[2].toLowerCase().split(";").includes("base64");
    const payload = uri[3].replace(RE_WHITESPACE, "");
    if (payload === "") {
      return { ok: false, code: "noData" };
    }
    bytes = isBase64 ? decodeBase64Url(payload) : percentBytes(uri[3]);
  } else if (found.source !== "raw") {
    return { ok: false, code: "badBase64" };
  } else {
    bytes = decodeBase64Url(found.value.replace(RE_WHITESPACE, ""));
  }
  if (bytes === null || bytes.length === 0) {
    return { ok: false, code: "badBase64" };
  }
  return { ok: true, image: describe(bytes, declaredMime, found.source) };
}

function describe(
  bytes: Uint8Array,
  declaredMime: string,
  source: ParsedImageInput["source"]
): ParsedImageInput {
  const sniffed = sniffMime(bytes);
  const warnings: InputWarning[] = [];
  const mime = sniffed ?? (declaredMime || "application/octet-stream");
  if (source === "raw" || declaredMime === "") {
    warnings.push("noPrefix");
  }
  if (sniffed !== null && declaredMime !== "" && sniffed !== declaredMime) {
    warnings.push("mismatch");
  }
  if (sniffed === null) {
    warnings.push("unknownType");
  }
  if (!mime.startsWith("image/")) {
    warnings.push("notImage");
  }
  return {
    mime,
    declaredMime,
    sniffedMime: sniffed,
    base64: encodeBase64(bytes),
    bytes,
    source,
    warnings,
  };
}
